import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Save,
  Description,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Email,
  Lock,
  Public,
  History,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import DocumentAccessHistory from './DocumentAccessHistory';
import DocumentVersionHistory from './DocumentVersionHistory';
import ReviewStatusChip from './ReviewStatusChip';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  category: string;
  tags: string[] | string; // Can be array or JSON string
  isConfidential: boolean;
  isPublic: boolean;
  version: number;
  caseId: string | null;
  clientId: string | null;
  uploadedBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  reviewStatus?: 'draft' | 'attorney_reviewed' | 'sent' | 'locked' | string | null;
}

interface DocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documentData?: Document | null;
  mode: 'view' | 'edit' | 'create';
  onSave: (documentData: Partial<Document>) => Promise<void>;
  /** Prefer wiring View to DocumentPreviewDialog; this opens the file if view-mode is used. */
  onOpenDocument?: (documentData: Document) => void;
}

const DocumentDialog: React.FC<DocumentDialogProps> = ({
  open,
  onClose,
  documentData,
  mode,
  onSave,
  onOpenDocument,
}) => {
  const [formData, setFormData] = useState<Partial<Document>>({
    title: '',
    description: '',
    documentType: 'other',
    category: '',
    tags: [],
    isConfidential: false,
    isPublic: false,
    caseId: null,
    clientId: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  useEffect(() => {
    if (documentData && mode !== 'create') {
      // Parse tags from JSON string to array
      let tags = [];
      if (documentData.tags) {
        if (typeof documentData.tags === 'string') {
          try {
            tags = JSON.parse(documentData.tags);
          } catch (error) {
            console.error('Error parsing tags:', error);
            tags = [];
          }
        } else if (Array.isArray(documentData.tags)) {
          tags = documentData.tags;
        }
      }

      setFormData({
        title: documentData.title,
        description: documentData.description,
        documentType: documentData.documentType,
        category: documentData.category,
        tags: tags,
        isConfidential: documentData.isConfidential,
        isPublic: documentData.isPublic,
        caseId: documentData.caseId,
        clientId: documentData.clientId,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        documentType: 'other',
        category: '',
        tags: [],
        isConfidential: false,
        isPublic: false,
        caseId: null,
        clientId: null,
      });
    }
  }, [documentData, mode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Please enter a document title');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      toast.success(mode === 'create' ? 'Document created successfully!' : 'Document updated successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to save document');
      console.error('Error saving document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getDocumentTypeIcon = (documentType: string) => {
    switch (documentType) {
      case 'image':
        return <Image />;
      case 'video':
        return <VideoFile />;
      case 'audio':
        return <AudioFile />;
      case 'archive':
        return <Archive />;
      case 'email_attachment':
        return <Email />;
      default:
        return <Description />;
    }
  };

  const getDocumentTypeColor = (documentType: string) => {
    switch (documentType) {
      case 'pleading':
        return 'primary';
      case 'evidence':
        return 'error';
      case 'correspondence':
        return 'info';
      case 'contract':
        return 'success';
      case 'invoice':
        return 'warning';
      default:
        return 'default';
    }
  };

  const documentTypes = [
    { value: 'pleading', label: 'Pleading' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'evidence', label: 'Evidence' },
    { value: 'court_filing', label: 'Court Filing' },
    { value: 'contract', label: 'Contract' },
    { value: 'agreement', label: 'Agreement' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'id_document', label: 'ID Document' },
    { value: 'medical_record', label: 'Medical Record' },
    { value: 'police_report', label: 'Police Report' },
    { value: 'expert_report', label: 'Expert Report' },
    { value: 'witness_statement', label: 'Witness Statement' },
    { value: 'photograph', label: 'Photograph' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
    { value: 'email_attachment', label: 'Email Attachment' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {mode === 'create' ? 'New Document' : 
             mode === 'edit' ? 'Edit Document Properties' : 'Document Properties'}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {mode === 'view' ? (
          // Legacy view = properties only; file viewing belongs in DocumentPreviewDialog
          <Box>
            <Grid container spacing={3}>
              {documentData && onOpenDocument && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<Description />}
                    onClick={() => onOpenDocument(documentData)}
                    sx={{ mb: 1 }}
                  >
                    Open document
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    View shows the file. Title, tags, and category are edited below or via Edit properties.
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {getDocumentTypeIcon(documentData?.documentType || '')}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {documentData?.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {documentData?.originalName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Document Type
                </Typography>
                <Chip
                  label={documentData?.documentType}
                  color={getDocumentTypeColor(documentData?.documentType || '') as any}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  File Size
                </Typography>
                <Typography variant="body2">
                  {documentData?.fileSize ? formatFileSize(documentData.fileSize) : 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Category
                </Typography>
                <Typography variant="body2">
                  {documentData?.category || 'Uncategorized'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Version
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {documentData?.version || 1}
                  </Typography>
                  {documentData?.id && (
                    <Button
                      size="small"
                      startIcon={<History />}
                      onClick={() => setVersionHistoryOpen(true)}
                    >
                      History
                    </Button>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" paragraph>
                  {documentData?.description || 'No description provided'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(() => {
                    let tags = [];
                    if (documentData?.tags) {
                      if (typeof documentData.tags === 'string') {
                        try {
                          tags = JSON.parse(documentData.tags);
                        } catch (error) {
                          console.error('Error parsing tags:', error);
                          tags = [];
                        }
                      } else if (Array.isArray(documentData.tags)) {
                        tags = documentData.tags;
                      }
                    }
                    
                    return tags.length > 0 ? (
                      tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No tags
                      </Typography>
                    );
                  })()}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Review status
                </Typography>
                {documentData?.id && (
                  <ReviewStatusChip
                    documentId={documentData.id}
                    reviewStatus={documentData.reviewStatus}
                    onChanged={(next) => {
                      documentData.reviewStatus = next;
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Security
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {documentData?.isConfidential && (
                    <Chip
                      icon={<Lock />}
                      label="Confidential"
                      color="error"
                      size="small"
                    />
                  )}
                  {documentData?.isPublic && (
                    <Chip
                      icon={<Public />}
                      label="Public"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Created: {documentData?.createdAt ? formatDate(documentData.createdAt) : 'Unknown'}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Last Updated: {documentData?.updatedAt ? formatDate(documentData.updatedAt) : 'Unknown'}
                </Typography>
              </Grid>

              {documentData?.id && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <DocumentAccessHistory documentId={documentData.id} />
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          // Edit/Create Mode
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Document Title *"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Document Type *</InputLabel>
                  <Select
                    value={formData.documentType}
                    onChange={(e) => handleInputChange('documentType', e.target.value)}
                    label="Document Type *"
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={(() => {
                    const tags = formData.tags;
                    if (Array.isArray(tags)) {
                      return tags;
                    }
                    if (typeof tags === 'string') {
                      try {
                        const parsed = JSON.parse(tags);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch (error) {
                        return [];
                      }
                    }
                    return [];
                  })()}
                  onChange={(event, newValue) => handleInputChange('tags', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags..."
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    const safeValue = Array.isArray(value) ? value : [];
                    return safeValue.map((option, index) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Confidentiality</InputLabel>
                  <Select
                    value={formData.isConfidential ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('isConfidential', e.target.value === 'true')}
                    label="Confidentiality"
                  >
                    <MenuItem value="false">Public</MenuItem>
                    <MenuItem value="true">Confidential</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={formData.isPublic ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('isPublic', e.target.value === 'true')}
                    label="Visibility"
                  >
                    <MenuItem value="false">Private</MenuItem>
                    <MenuItem value="true">Public</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {mode === 'view' ? 'Close' : 'Cancel'}
        </Button>
        {(mode === 'edit' || mode === 'create') && (
          <Button
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Document'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {documentData?.id && (
      <DocumentVersionHistory
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        documentId={documentData.id}
        documentTitle={documentData.title}
      />
    )}
    </>
  );
};

export default DocumentDialog; 